import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { ArrowRight, Loader2, Eye, EyeOff, Mail, KeyRound, CheckCircle2, X, LifeBuoy } from "lucide-react";
import { toast } from "sonner";

const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").join(" ");
  return fallback;
};

export default function Login() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [showContact, setShowContact] = useState(false);

  if (user) return <Navigate to={user.role === "client" ? "/portal" : "/"} />;

  const submit = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    try {
      const u = await login(email, password, remember);
      toast.success(`Welcome back, ${u.name}!`);
      nav(u.role === "client" ? "/portal" : "/");
    } catch (err) {
      toast.error(errText(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" style={{ fontFamily: "Poppins, sans-serif", background: "#F4F6F4" }}>
      {/* Brand panel */}
      <div className="lg:w-[46%] relative overflow-hidden flex flex-col justify-between p-6 lg:p-14" style={{ background: "linear-gradient(160deg, #145353 0%, #0D3B3B 100%)" }}>
        <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full opacity-10" style={{ background: "#72BE44" }} />
        <div className="absolute -left-16 bottom-10 w-72 h-72 rounded-full opacity-[0.07]" style={{ background: "#72BE44" }} />
        <div className="relative">
          <img src="/ptespl-logo-white.png" alt="PT Ecological Services Pvt. Ltd." className="w-36 lg:w-52 -ml-2" data-testid="login-logo" />
        </div>
        <div className="relative mt-10 lg:mt-0 hidden lg:block">
          <div className="text-[11px] uppercase tracking-[0.3em] font-semibold mb-4" style={{ color: "#72BE44" }}>PTESPL</div>
          <h2 className="text-white text-2xl lg:text-4xl font-bold leading-snug tracking-tight">
            Environmental Compliance<br />Managing Partner <span style={{ color: "#72BE44" }}>(ECMP)</span>
          </h2>
          <p className="text-white/70 text-sm mt-4 max-w-md leading-relaxed">
            EC · MPCB Consents · Post-EC Compliance · PMR · Environmental Systems — one professional cockpit for your entire compliance lifecycle.
          </p>
        </div>
        <div className="relative text-white/40 text-xs mt-4 lg:mt-0 hidden lg:block">© {new Date().getFullYear()} PT Ecological Services Pvt. Ltd. All rights reserved.</div>
      </div>

      {/* Form panel */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl p-8 lg:p-10" style={{ boxShadow: "0 20px 60px rgba(20,83,83,0.12)" }}>
            <img src="/ptespl-logo.png" alt="PTESPL" className="w-20 mb-5 lg:hidden" />
            <h1 className="text-2xl font-bold tracking-tight" style={{ color: "#145353" }}>Welcome Back!</h1>
            <p className="text-sm mt-1 mb-8" style={{ color: "#58595B" }}>Manage Your Environmental Compliance Projects.</p>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-xs font-semibold" style={{ color: "#58595B" }}>Username / Email ID</label>
                <div className="relative mt-1.5">
                  <Mail className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: "#72BE44" }} />
                  <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                    placeholder="you@company.com"
                    className="w-full pl-10 pr-4 py-3 text-sm bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:bg-white focus:border-[#72BE44] focus:ring-2 focus:ring-[#72BE44]/15 transition" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold" style={{ color: "#58595B" }}>Password</label>
                <div className="relative mt-1.5">
                  <KeyRound className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: "#72BE44" }} />
                  <input data-testid="login-password" type={showPwd ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required
                    placeholder="••••••••"
                    className="w-full pl-10 pr-11 py-3 text-sm bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:bg-white focus:border-[#72BE44] focus:ring-2 focus:ring-[#72BE44]/15 transition" />
                  <button type="button" onClick={() => setShowPwd(!showPwd)} data-testid="toggle-password-visibility"
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-stone-400 hover:text-[#145353] transition">
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between text-xs pt-1">
                <label className="flex items-center gap-2 cursor-pointer select-none" style={{ color: "#58595B" }}>
                  <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="remember-me"
                    className="w-3.5 h-3.5 rounded accent-[#72BE44]" />
                  Remember Me
                </label>
                <button type="button" onClick={() => setShowForgot(true)} data-testid="forgot-password-link"
                  className="font-semibold hover:underline" style={{ color: "#145353" }}>
                  Forgot Password?
                </button>
              </div>

              <button data-testid="login-submit" type="submit" disabled={loading}
                className="w-full flex items-center justify-center gap-2 text-white font-semibold py-3.5 rounded-xl transition disabled:opacity-50 hover:brightness-110 active:scale-[0.99]"
                style={{ background: "linear-gradient(135deg, #72BE44, #5CA635)" }}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Login <ArrowRight className="w-4 h-4" /></>}
              </button>
            </form>

            <div className="mt-6 pt-5 border-t border-stone-100 flex items-center justify-between text-xs">
              <span style={{ color: "#58595B" }}>Need an account? Ask your Super Admin.</span>
              <button type="button" onClick={() => setShowContact(true)} data-testid="contact-admin-link"
                className="flex items-center gap-1 font-semibold hover:underline" style={{ color: "#72BE44" }}>
                <LifeBuoy className="w-3.5 h-3.5" /> IT Support
              </button>
            </div>
          </div>
          <div className="text-center text-[11px] mt-5" style={{ color: "#58595B" }} data-testid="consent-notice">
            By logging in you consent to PTESPL storing your project compliance data for service delivery.
          </div>
        </div>
      </div>

      {showForgot && <ForgotPasswordModal onClose={() => setShowForgot(false)} prefillEmail={email} />}
      {showContact && <ContactAdminModal onClose={() => setShowContact(false)} />}
    </div>
  );
}

function ContactAdminModal({ onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-2xl p-8 w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="contact-admin-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg" style={{ color: "#145353" }}>Contact Administrator</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-600"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-sm mb-4" style={{ color: "#58595B" }}>
          For new accounts, locked accounts or login issues, contact your ECMP administrator:
        </p>
        <a href="mailto:admin@ecmp.com?subject=ECMP CRM Access Request" className="block text-center py-3 rounded-xl text-white font-semibold text-sm" style={{ background: "#145353" }}>
          Email IT Support
        </a>
      </div>
    </div>
  );
}

function ForgotPasswordModal({ onClose, prefillEmail }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState(prefillEmail || "");
  const [otp, setOtp] = useState("");
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);

  const requestOtp = async (e) => {
    e?.preventDefault?.();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If this email is registered, a 6-digit code has been sent");
      setStep(2);
    } catch (err) { toast.error(errText(err, "Could not send the code — try again")); }
    finally { setBusy(false); }
  };

  const doReset = async (e) => {
    e?.preventDefault?.();
    if (pwd !== pwd2) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { email, otp, new_password: pwd, confirm_password: pwd2 });
      setStep(3);
    } catch (err) { toast.error(errText(err, "Reset failed")); }
    finally { setBusy(false); }
  };

  const inputCls = "w-full px-4 py-3 text-sm bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:bg-white focus:border-[#72BE44] focus:ring-2 focus:ring-[#72BE44]/15 transition";

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-2xl p-8 w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="forgot-password-modal">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-bold text-lg" style={{ color: "#145353" }}>
            {step === 1 ? "Forgot Password" : step === 2 ? "Verify & Reset" : "Password Reset!"}
          </h3>
          <button onClick={onClose} data-testid="forgot-close" className="text-stone-400 hover:text-stone-600"><X className="w-4 h-4" /></button>
        </div>

        {step === 1 && (
          <form onSubmit={requestOtp} className="space-y-4 mt-4">
            <p className="text-sm" style={{ color: "#58595B" }}>Enter your registered email — we'll send a 6-digit verification code (valid 10 minutes).</p>
            <input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className={inputCls} />
            <button type="submit" disabled={busy} data-testid="forgot-send-otp"
              className="w-full py-3 rounded-xl text-white font-semibold text-sm disabled:opacity-50" style={{ background: "#72BE44" }}>
              {busy ? "Sending..." : "Send Verification Code"}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={doReset} className="space-y-4 mt-4">
            <p className="text-sm" style={{ color: "#58595B" }}>Code sent to <b>{email}</b>. Enter it below with your new password.</p>
            <input data-testid="forgot-otp" required value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="6-digit code" maxLength={6}
              className={inputCls + " text-center tracking-[0.5em] font-bold text-lg"} />
            <div className="relative">
              <input data-testid="forgot-new-password" type={showPwd ? "text" : "password"} required value={pwd} onChange={(e) => setPwd(e.target.value)}
                placeholder="New password (min 8 chars, letter + number)" className={inputCls + " pr-11"} />
              <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400">
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <input data-testid="forgot-confirm-password" type={showPwd ? "text" : "password"} required value={pwd2} onChange={(e) => setPwd2(e.target.value)}
              placeholder="Confirm new password" className={inputCls} />
            <button type="submit" disabled={busy} data-testid="forgot-reset-submit"
              className="w-full py-3 rounded-xl text-white font-semibold text-sm disabled:opacity-50" style={{ background: "#145353" }}>
              {busy ? "Resetting..." : "Reset Password"}
            </button>
            <button type="button" onClick={requestOtp} className="w-full text-xs hover:underline" style={{ color: "#72BE44" }}>Resend code</button>
          </form>
        )}

        {step === 3 && (
          <div className="text-center py-6" data-testid="forgot-success">
            <CheckCircle2 className="w-14 h-14 mx-auto mb-4" style={{ color: "#72BE44" }} />
            <p className="font-semibold" style={{ color: "#145353" }}>Your password has been reset successfully.</p>
            <p className="text-sm mt-1 mb-6" style={{ color: "#58595B" }}>You can now log in with your new password.</p>
            <button onClick={onClose} data-testid="forgot-done" className="px-8 py-3 rounded-xl text-white font-semibold text-sm" style={{ background: "#72BE44" }}>
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
